import {
  OrderEvent,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  RequestStatus,
} from '__generated__/atlas-types'

export const TableColumns = {
  Selection: 'selection',
  Actions: 'actions',
  OrderCreatedDate: 'orderCreatedDate',
  RetailerName: 'retailerName',
  PO: 'poNumber',
  PaymentStatus: 'paymentStatus',
  TotalDue: 'totalDue',
  PaidAmount: 'paidAmount',
  RemainingBalance: 'remainingBalance',
  Currency: 'currency',
  FirstRequest: 'firstRequest',
  LastNotified: 'lastNotified',
  LastTransaction: 'lastTransaction',
  RequestStatus: 'requestStatus',
  OrderStatus: 'orderStatus',
  TrackingNumber: 'trackingNumber',
  SalesRep: 'salesRep',
  DueDate: 'dueDate',
  PaymentMethod: 'paymentMethod',
  Reminders: 'reminders',
  Event: 'event',
  ShipStart: 'shipStart',
  ShipComplete: 'shipComplete',
  Linesheet: 'linesheet',
  TotalOrderValue: 'totalOrderValue',
  SubtotalWholesale: 'subtotalWholesale',
  OrderDiscount: 'orderDiscount',
  ShippingFees: 'shippingFees',
  Taxes: 'taxes',
  CustomFees: 'customFees',
  TotalUnits: 'totalUnits',
} as const
export type TableColumnsType = (typeof TableColumns)[keyof typeof TableColumns]
export enum RemindersType {
  WARNING = 'warning',
  ALERT = 'alert',
}

export type RemindersCellData = {
  message: string
  type?: RemindersType
}

export type LinesheetCellValue = {
  id: string
  name: string
}

export type TableRowData = {
  // Table Related Data
  [TableColumns.OrderCreatedDate]: Date
  [TableColumns.RetailerName]: string
  [TableColumns.PO]: string
  [TableColumns.PaymentStatus]: PaymentStatusesType | string
  [TableColumns.Reminders]: RemindersCellData
  [TableColumns.TotalDue]: number
  [TableColumns.PaidAmount]: number
  [TableColumns.RemainingBalance]: number
  [TableColumns.DueDate]: Date | undefined
  [TableColumns.Currency]: string
  [TableColumns.FirstRequest]: Date | undefined
  [TableColumns.LastNotified]: Date | undefined
  [TableColumns.LastTransaction]: Date | undefined
  [TableColumns.RequestStatus]: RequestStatusesType | string
  [TableColumns.OrderStatus]: OrderStatusesType | string
  [TableColumns.PaymentMethod]: PaymentMethod['name'] | string
  [TableColumns.TrackingNumber]: string
  [TableColumns.SalesRep]: string
  [TableColumns.Event]: OrderEvent | undefined
  [TableColumns.ShipStart]: Date | undefined
  [TableColumns.ShipComplete]: Date | undefined
  [TableColumns.Linesheet]: LinesheetCellValue
  [TableColumns.TotalOrderValue]: number
  [TableColumns.SubtotalWholesale]: number
  [TableColumns.OrderDiscount]: number
  [TableColumns.ShippingFees]: number
  [TableColumns.Taxes]: number
  [TableColumns.CustomFees]: number
  [TableColumns.TotalUnits]: number
  // General Order Data needed for actions
  id: string
  webOrderLookupId: string
  shippingEmail?: string | null
  retailerId?: string | null
  brandId: string
  brandName: string
  type: PaymentTypesType
  billingFirstAddress?: string | null
  billingSecondAddress?: string | null
  billingCountry?: string | null
  billingCity?: string | null
  billingZip?: string | null
}

// Needed for being able to distinguish between joor pay orders and external orders
export const PaymentTypes = {
  JoorPayment: 'JOOR_PAYMENT',
  ExternalPayment: 'EXTERNAL_PAYMENT',
} as const
export type PaymentTypesType = (typeof PaymentTypes)[keyof typeof PaymentTypes]

export const PaymentStatuses: Record<
  PaymentStatus,
  'Paid' | 'Unpaid' | 'Partially Paid' | 'Processing'
> = {
  FULLY_PAID: 'Paid',
  NOT_PAID: 'Unpaid',
  PARTIALLY_PAID: 'Partially Paid',
  PROCESSING: 'Processing',
} as const
export type PaymentStatusesType =
  (typeof PaymentStatuses)[keyof typeof PaymentStatuses]

export const RequestStatuses: Record<RequestStatus, 'Sent' | 'Not Sent'> = {
  SENT: 'Sent',
  NOT_SENT: 'Not Sent',
} as const
export type RequestStatusesType =
  (typeof RequestStatuses)[keyof typeof RequestStatuses]

export const OrderStatuses: Record<
  OrderStatus,
  | 'Cancelled'
  | 'Draft'
  | 'Pending'
  | 'Returned'
  | 'Approved'
  | 'Shipped'
  | 'Notes'
  | 'All Notes'
  | 'Notes Without Quantities'
  | 'Notes With Quantities'
> = {
  [OrderStatus.CANCELLED]: 'Cancelled',
  [OrderStatus.IN_PROGRESS]: 'Draft',
  [OrderStatus.PENDING]: 'Pending',
  [OrderStatus.RETURNED]: 'Returned',
  [OrderStatus.APPROVED]: 'Approved',
  [OrderStatus.SHIPPED]: 'Shipped',
  [OrderStatus.NOTES]: 'Notes',
  [OrderStatus.NOTES_WITHOUT_QUANTITIES]: 'Notes Without Quantities',
  [OrderStatus.NOTES_WITH_QUANTITIES]: 'Notes With Quantities',
  [OrderStatus.ALL_NOTES]: 'All Notes',
} as const

export type OrderStatusesType =
  (typeof OrderStatuses)[keyof typeof OrderStatuses]

export const LoadValues = {
  partial: 'partial',
  all: 'all',
} as const

export type LoadValue = (typeof LoadValues)[keyof typeof LoadValues]
